import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { CCompareResponse } from '@arrowise/shared';
import { compareService } from '../../services';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Users } from 'lucide-react';
import CompareListItem from './CompareListItem';

export default function CompareList() {
    const { positionId } = useParams();
    const [searchParams] = useSearchParams();

    const [data, setData] = useState<CCompareResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const order = (searchParams.get('order') as 'best' | 'worst') || 'best';

    const displayedItems = useMemo(() => {
        if (!data) return [];
        return order === 'best' ? data.items : [...data.items].reverse();
    }, [data, order]);

    useEffect(() => {
        if (!positionId) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await compareService.getCompareData(positionId, 'best');
                setData(data);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load candidates');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [positionId]);

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                    <Card key={i} className="animate-pulse bg-card border-border">
                        <CardContent className="p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <Skeleton className="w-10 h-10 rounded-full bg-muted" />
                                <div className="flex-1">
                                    <Skeleton className="h-5 w-3/4 mb-2 bg-muted" />
                                    <Skeleton className="h-4 w-1/2 bg-muted" />
                                </div>
                            </div>
                            <Skeleton className="h-4 w-1/2 mb-2 bg-muted" />
                            <Skeleton className="h-16 w-full bg-muted" />
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <Card className="border-destructive/20 bg-destructive/5">
                <CardContent className="p-6">
                    <div className="flex items-center space-x-3 text-destructive">
                        <AlertCircle className="h-6 w-6" />
                        <div>
                            <h3 className="font-semibold">Error Loading Candidates</h3>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data || displayedItems.length === 0) {
        return (
            <Card className="border-dashed border-2 border-border bg-muted/20">
                <CardContent className="p-12 text-center">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No Candidates Yet</h3>
                    <p className="text-sm text-muted-foreground">
                        Completed candidate sessions will appear here for comparison.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedItems.map((item, index) => (
                <div
                    key={item.sessionId}
                    className="animate-in fade-in-0 slide-in-from-bottom-4"
                    style={{ animationDelay: `${index * 100}ms` }}
                >
                    <CompareListItem item={item} positionId={positionId} />
                </div>
            ))}
        </div>
    );
}
